import { getCoreType } from '@/common/constants/core'
import { hasFullSession } from '@/common/plugins/axios'
import { apiService } from '@/services/api.service'
import type { ExecuteScriptRequest, JsRequest } from '@/services/websocket/types'
import { getWebSocketService } from '@/services/websocket/websocket.service'
import type En from '@/common/classes/En.ts'
import {
  normalizePlatformSessionId,
  runWithJsRequestSessionId,
} from '@/common/utils/jsRequestSessionContext'
import { startWorkflowMeasure } from '@/common/utils/performanceTrace'
import { reportError } from '@/common/utils/errorReporter'
import type { ExecuteScriptParams, JSResult } from '@/services/script/types.ts'
import { MiddleExecutionRequiredError } from '@/services/parse/MiddleExecutionRequiredError'

const activeExecuteScriptDataStack: any[] = []

function resolveExecuteScriptFieldMap(currentData: unknown): Record<string, unknown> | null {
  if (!currentData || typeof currentData !== 'object') {
    return null
  }
  const fieldsLevel1 = (currentData as { fields?: unknown }).fields
  if (!fieldsLevel1 || typeof fieldsLevel1 !== 'object') {
    return null
  }
  const fieldsLevel2 = (fieldsLevel1 as { fields?: unknown }).fields
  if (!fieldsLevel2 || typeof fieldsLevel2 !== 'object' || Array.isArray(fieldsLevel2)) {
    return null
  }
  return fieldsLevel2 as Record<string, unknown>
}

export function getActiveExecuteScriptCurrentData(): any {
  return activeExecuteScriptDataStack.length > 0
    ? activeExecuteScriptDataStack[activeExecuteScriptDataStack.length - 1]
    : null
}

export class ScriptService {
  private wsService = getWebSocketService()

  async executeScript(
    params: ExecuteScriptParams,
    en?: En, // En instance for local execution
  ): Promise<JSResult> {
    const endMeasure = startWorkflowMeasure('scriptService.executeScript', {
      sessionId: params.sessionId,
      elementId: params.elementId,
      level: params.level,
      scriptName: params.scriptName,
      hasSocketId: Boolean(this.wsService.getSocketId()),
    })
    try {
      let socketId = this.wsService.getSocketId()

      if (!socketId) {
        if (!this.wsService.isConnected()) {
          if (!hasFullSession()) {
            throw new Error('WebSocket not connected. Cannot execute script.')
          }

          const token = localStorage.getItem('auth_token') || undefined
          const endConnectMeasure = startWorkflowMeasure('scriptService.ws.connect', {
            elementId: params.elementId,
            level: params.level,
          })
          await this.wsService.connect(token)
          endConnectMeasure()
        }

        const endWaitSocketMeasure = startWorkflowMeasure('scriptService.ws.waitForSocketId', {
          elementId: params.elementId,
          level: params.level,
        })
        socketId = await this.wsService.waitForSocketId(5000)
        endWaitSocketMeasure()
      }

      if (!socketId) {
        throw new Error('WebSocket not connected. Cannot execute script.')
      }

      const request: ExecuteScriptRequest = {
        type: params.type || getCoreType(),
        sessionId: params.sessionId,
        socketId,
        isClone: params.isClone || false,
        currentData: params.currentData,
        elementId: params.elementId,
        level: params.level,
        scriptName: params.scriptName,
        scriptBody: params.scriptBody,
        cloneOf: params.cloneOf,
      }

      const isRequestTimeout = (err: any) =>
        err?.code === 'ECONNABORTED' || /timeout/i.test(String(err?.message ?? ''))

      const isMiddleResponseTimeout = (err: any) => {
        const code = Number(err?.code ?? err?.response?.data?.code)

        return code === 2002
      }

      const isTimeoutError = (err: any) => isRequestTimeout(err) || isMiddleResponseTimeout(err)

      const doRequest = () => apiService.platformExecuteScript(request)

      let response
      activeExecuteScriptDataStack.push(params.currentData)
      try {
        response = await doRequest()
      } catch (error: any) {
        if (error?.response?.status === 400) {
          console.warn('Server returned 400 for executeScript - might be a client-only script')
          throw error
        }
        if (!isTimeoutError(error)) throw error
        try {
          response = await doRequest()
        } catch (secondError: any) {
          if (!isTimeoutError(secondError)) throw secondError
          const { showTimeoutRecoveryDialog } = await import('@/services/messages/dialog-manager')
          const choice = await showTimeoutRecoveryDialog({
            title: 'Превышено время ожидания',
            message: 'Запрос не успел выполниться. Обновите страницу или повторите попытку.',
          })
          if (choice === 'reload') {
            window.location.reload()
            // Перезагрузка прерывает текущий поток, но для типобезопасности завершаем как ошибку.
            throw new Error('Reload requested')
          }
          response = await doRequest()
        }
      } finally {
        activeExecuteScriptDataStack.pop()
      }

      const result: JSResult = response.data

      // If server returned client_script, execute it locally
      if (result.client_script) {
        if (!en) {
          console.warn(
            'Client script returned but no En instance provided. Script will not be executed.',
          )
          return result
        }

        // выполнение скрипта локально???? если он был возвращен из мидла???? client_script
        try {
          const fieldsForEn = resolveExecuteScriptFieldMap(params.currentData)
          if (fieldsForEn) {
            en._resetData(fieldsForEn)
          }
          const endClientScriptMeasure = startWorkflowMeasure('scriptService.executeClientScript', {
            elementId: params.elementId,
            level: params.level,
          })
          const scriptSessionId = normalizePlatformSessionId(params.sessionId)
          const localResult = await runWithJsRequestSessionId(scriptSessionId, () =>
            en.run(result.client_script),
          )
          endClientScriptMeasure()
          // Merge local result with server result
          return {
            ...result,
            local_result: localResult,
          }
        } catch (error) {
          if (!(error instanceof MiddleExecutionRequiredError)) {
            console.error('Error executing client script:', error)
            reportError(error, {
              source: 'script.service',
              elementId: params.elementId != null ? String(params.elementId) : '',
              level: params.level != null ? String(params.level) : '',
            })
          }
          return {
            ...result,
            error: error instanceof Error ? error.message : String(error),
          }
        }
      }

      return result
    } finally {
      endMeasure()
    }
  }

  registerJsRequestHandler(handler: (request: JsRequest) => Promise<any>): () => void {
    return this.wsService.onJsRequest(handler)
  }

  getSocketId(): string | null {
    return this.wsService.getSocketId()
  }
}

export const scriptService = new ScriptService()
export default scriptService
